import React, { useEffect, useState, useContext } from "react";
import { useAuthFetch } from "../../hooks/useAuthFetch";
import { ReportingSiteFilter } from "./filters/siteFilter";
import { AuthContext } from "../../contexts/AuthContext";
import { ReportingActivityTypeFilter } from "./filters/activityTypeFilter";
import { ReportingUserFilter } from "./filters/userFilter";
import { ReportingCustomerFilter } from "./filters/customerFilter";
import { UserProfile } from "../../components/profile";
import Datepicker from "react-tailwindcss-datepicker";
import { formatActivityDateTime } from "../../utils/dateTimeUtil";
import { ReportingActivityDetails } from "./details";
import { Table } from "../../components/table";
import { DocumentTextIcon } from "@heroicons/react/24/outline";
import { PageHeading } from "../../components/pageHeading";

function Reporting() {
  const { authContext } = useContext(AuthContext);
  const orgId = authContext.organization.orgId;
  const authFetch = useAuthFetch();

  const [isLoading, setIsLoading] = useState(true);
  const [selectedReportingActivityId, setSelectedReportingActivityId] =
    useState();

  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const toggleState = (setState, state) => {
    setState(!state);
  };

  const handleDetailsClick = (id) => {
    setSelectedReportingActivityId(id);
    toggleState(setIsDetailsOpen, isDetailsOpen);
  };

  /// Customers dropdown ///
  const [selectedCustomers, setSelectedCustomers] = useState([]);

  const handleSelectedCustomers = (optionId) => {
    setSelectedCustomers((currentSelected) => {
      if (currentSelected.includes(optionId)) {
        return currentSelected.filter((item) => item !== optionId);
      } else {
        return [...currentSelected, optionId];
      }
    });
  };

  const clearSelectedCustomers = () => {
    setSelectedCustomers([]);
  };

  /// Sites dropdown ///
  const [selectedSites, setSelectedSites] = useState([]);
  const [sitesOptions, setSitesOptions] = useState([]);

  const handleSelectedSites = (optionId) => {
    setSelectedSites((currentSelected) => {
      if (currentSelected.includes(optionId)) {
        return currentSelected.filter((item) => item !== optionId);
      } else {
        return [...currentSelected, optionId];
      }
    });
  };

  const clearSelectedSites = () => {
    setSelectedSites([]);
  };

  /// Actitivity type dropdown ///
  const [selectedActivityTypes, setSelectedActivityTypes] = useState([]);

  const handleSelectedActivityTypes = (optionId) => {
    setSelectedActivityTypes((currentSelected) => {
      if (currentSelected.includes(optionId)) {
        return currentSelected.filter((item) => item !== optionId);
      } else {
        return [...currentSelected, optionId];
      }
    });
  };

  const clearSelectedActivityTypes = () => {
    setSelectedActivityTypes([]);
  };

  /// Users dropdown ///
  const [selectedUsers, setSelectedUsers] = useState([]);

  const handleSelectedUsers = (optionId) => {
    setSelectedUsers((currentSelected) => {
      if (currentSelected.includes(optionId)) {
        return currentSelected.filter((item) => item !== optionId);
      } else {
        return [...currentSelected, optionId];
      }
    });
  };

  const clearSelectedUsers = () => {
    setSelectedUsers([]);
  };

  /// Date picker ///
  const [dateRange, setDateRange] = useState({
    startDate: new Date().toISOString().split("T")[0],
    endDate: new Date().toISOString().split("T")[0],
  });

  const handleselectedDateRange = (selectedDateRange) => {
    console.log("Selected dates:", selectedDateRange);
    setDateRange(selectedDateRange);
    console.log(typeof selectedDateRange.startDate);
  };

  /// Get the data for the table ///
  const [tableData, setTableData] = useState([]);
  const constructApiURL = (
    selectedCustomers,
    selectedSites,
    selectedActivityTypes,
    selectedUsers,
    dateRange,
  ) => {
    let baseUrl = `${process.env.REACT_APP_API_URL}/v1/reporting/activities?org_id=${orgId}`;

    if (selectedCustomers.length > 0) {
      // Join the selectedSites array into a comma-separated string
      const customerIds = selectedCustomers.join(",");
      baseUrl += `&customer_ids=${customerIds}`;
    }

    if (selectedSites.length > 0) {
      // Join the selectedSites array into a comma-separated string
      const siteIds = selectedSites.join(",");
      baseUrl += `&site_ids=${siteIds}`;
    }

    if (selectedActivityTypes.length > 0) {
      // Join the selectedSites array into a comma-separated string
      const activityTypeIds = selectedActivityTypes.join(",");
      baseUrl += `&activity_type_ids=${activityTypeIds}`;
    }

    if (selectedUsers.length > 0) {
      // Join the selectedSites array into a comma-separated string
      const userIds = selectedUsers.join(",");
      baseUrl += `&user_ids=${userIds}`;
    }

    if (dateRange.startDate) {
      baseUrl += `&start_date=${dateRange.startDate}`;
    }

    // Check if `endDate` is a Date object before using `toISOString`
    if (dateRange.endDate) {
      baseUrl += `&end_date=${dateRange.endDate}`;
    }

    console.log("baseUrl", baseUrl);

    return baseUrl;
  };

  const fetchReportingData = async () => {
    if (!orgId) return;

    const currentApiURL = constructApiURL(
      selectedCustomers,
      selectedSites,
      selectedActivityTypes,
      selectedUsers,
      dateRange,
    );

    const fetchPromise = authFetch(currentApiURL)
      .then(async (response) => {
        if (response.ok) {
          const data = await response.json();
          setTableData(data);
        } else {
          // Handle HTTP errors e.g., 404, 500 etc.
          console.error("HTTP Error: ", response.statusText);
          // Optionally, update state to display an error message
        }
      })
      .catch((error) => {
        // Handle network errors, parsing errors, etc.
        console.error("Fetching error: ", error.message);
        // Optionally, update state to display an error message
      });

    const timerPromise = new Promise((resolve) => setTimeout(resolve, 500));

    Promise.all([fetchPromise, timerPromise]).then(() => {
      setIsLoading(false);
    });
  };

  useEffect(() => {
    setIsLoading(true);
    fetchReportingData();
  }, [
    selectedCustomers,
    selectedSites,
    selectedActivityTypes,
    selectedUsers,
    dateRange,
  ]);

  useEffect(() => {
    // Update the selected sites when selected customers change
    setSelectedSites((currentSelectedSites) =>
      currentSelectedSites.filter((site) =>
        sitesOptions.some((option) => option.id === site),
      ),
    );
  }, [selectedCustomers, sitesOptions]);

  /// Set up table cols ///
  const columns = React.useMemo(
    () => [
      {
        Header: "Customer mame",
        accessor: "customer_name",
        Cell: ({ row }) => {
          // Clock in/out rows are synthetic (id like "clockin-42") and have no
          // detail record, so they aren't clickable into the detail sheet.
          const isClock = String(row.original.id).startsWith("clock");
          return isClock ? (
            <span className="text-gray-500 capitalize">
              {row.original.first_name} {row.original.customer_name}
            </span>
          ) : (
            <a
              onClick={() => handleDetailsClick(row.original.id)}
              className="text-gray-500 hover:text-gray-600 hover:underline capitalize cursor-pointer"
            >
              {row.original.first_name} {row.original.customer_name}
            </a>
          );
        },
      },
      {
        Header: "Site name",
        accessor: "site_name",
      },
      {
        Header: "Activity type",
        accessor: "activity_type",
      },
      {
        Header: "Activity",
        accessor: "activity",
      },
      {
        Header: "User",
        accessor: (row) => `${row.first_name} ${row.last_name}`, // Creating a composite accessor
        Cell: ({ row }) => (
          <div className="flex items-center">
            <UserProfile
              imageUrl={row.original.profile_url || ""}
              size="small"
              firstName={row.original.firstName || ""}
              lastName={row.original.lastName || ""}
              useInitials={false}
            />
            <span>
              {row.original.first_name} {row.original.last_name}
            </span>
          </div>
        ),
      },
      {
        Header: "Date",
        accessor: "activity_date",
        Cell: ({ value }) => formatActivityDateTime(value),
      },
    ],
    [],
  );

  return (
    <div>
      <main className="py-6">
        <div className="px-4 sm:px-6 lg:px-6">
          <PageHeading title="Activities" />
          <div className="flex items-center gap-3 mb-4 ml-1">
            <ReportingCustomerFilter
              selected={selectedCustomers}
              onUpdate={handleSelectedCustomers}
              onClear={clearSelectedCustomers}
            />
            <ReportingSiteFilter
              selected={selectedSites}
              onUpdate={handleSelectedSites}
              onClear={clearSelectedSites}
              customerIds={selectedCustomers}
              setSitesOptions={setSitesOptions}
            />
            <ReportingActivityTypeFilter
              selected={selectedActivityTypes}
              onUpdate={handleSelectedActivityTypes}
              onClear={clearSelectedActivityTypes}
            />
            <ReportingUserFilter
              selected={selectedUsers}
              onUpdate={handleSelectedUsers}
              onClear={clearSelectedUsers}
            />
            <div className="w-fit">
              <Datepicker
                startFrom={new Date("2024-01-05")}
                showShortcuts={true}
                inputClassName={
                  "h-9 w-64 inline-flex rounded-md px-3 items-center text-sm leading-none tracking-normal font-normal text-gray-600 dark:text-gray-200 placeholder-gray-500 dark:placeholder-gray-400 hover:text-gray-800 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-white/[0.06] bg-gray-50 dark:bg-gray-800 border-0 pr-10 focus:outline-none"
                }
                separator={"to"}
                value={dateRange}
                onChange={handleselectedDateRange}
              />
            </div>
          </div>

          <Table
            data={tableData}
            columns={columns}
            defaultSort={{ id: "activity_date", desc: true }}
            isLoading={isLoading}
            emptyState={
              <div className="flex flex-col items-center justify-center h-72">
                <DocumentTextIcon className="h-10 w-10 text-gray-300 dark:text-gray-600" />
                <p className="mt-3 text-sm text-gray-400 dark:text-gray-500 text-center">
                  No reports for the selected filters
                </p>
              </div>
            }
          />
        </div>
      </main>
      <ReportingActivityDetails
        isOpen={isDetailsOpen}
        toggleOpen={handleDetailsClick}
        selectedReportingActivityId={selectedReportingActivityId}
      />
    </div>
  );
}

export { Reporting };
